/**
 * Conformance hooks (spec 017): in development and test builds the editor
 * exposes what its renderers show, so tests can hold them to spec 012's
 * `evaluateFrame` with `assertConforms`.
 *
 * - `window.__storymotion.canvas.getFrameState()`: the editing canvas
 *   (`SceneCanvas`) at the playhead.
 * - `window.__storymotion.preview.{seek, pause, getFrameState}`: the active
 *   scene `PlayerHost` (forwarding to its `SceneRuntime`).
 *
 * Production builds register neither.
 */

import type { FrameState } from '../../../Shared/src/@types';

export interface CanvasProbe {
  getFrameState(): FrameState | null;
}

export interface PreviewProbe {
  seek(ms: number): void;
  pause(): void;
  getFrameState(): FrameState | null;
}

export interface StoryMotionHooks {
  canvas?: CanvasProbe;
  preview?: PreviewProbe;
}

declare global {
  interface Window {
    __storymotion?: StoryMotionHooks;
  }
}

/** True outside production builds (Vite's mode; Vitest runs as `test`). */
export function conformanceHooksEnabled(): boolean {
  return import.meta.env.MODE !== 'production';
}

/**
 * Register `probe` as `window.__storymotion[slot]`. Returns the unregister
 * function, which clears the slot only while it still holds this probe
 * (a later mount owns it otherwise). A no-op in production.
 */
export function registerProbe<K extends keyof StoryMotionHooks>(
  slot: K,
  probe: NonNullable<StoryMotionHooks[K]>,
): () => void {
  if (!conformanceHooksEnabled() || typeof window === 'undefined') return () => {};
  const hooks = (window.__storymotion ??= {});
  hooks[slot] = probe;
  return () => {
    if (window.__storymotion?.[slot] === probe) delete window.__storymotion[slot];
  };
}
